import { useMemo, useState } from 'react'
import {
  Calendar,
  CheckCircle,
  DollarSign,
  Pencil,
  ReceiptText,
  Tag,
  Trash2,
  XCircle,
} from 'lucide-react'
import { useForm } from '@tanstack/react-form'
import { useExpensesStore } from '../store/expensesStore'
import { useApproveExpenseMutation, useExpenseReceiptQuery } from '../hooks'
import { ExpenseForm } from './ExpenseForm'
import { DeleteExpense } from './DeleteExpense'
import {
  InteractiveStepper,
  InteractiveStepperIndicator,
  InteractiveStepperItem,
  InteractiveStepperSeparator,
  InteractiveStepperTitle,
} from '@/components/ui/stepper'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Modal } from '@/components/custom/modal'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate, transformDateToTimezone } from '@/utils/timeZoneUtils'
import { useUsersStore } from '@/features/users/store/usersStore'
import { useIsMobile } from '@/hooks'
import { useAuthStore } from '@/features/auth/store/authStore'
import DatePicker from '@/components/custom/date-picker'
import { FormTextarea } from '@/components/custom/form-textarea'
import { usePermissions } from '@/utils/permissions'

interface ExpenseDetailsProps {
  id: string | null
  open: boolean
  onClose: () => void
}

interface ApprovalData {
  notes: string
  reimbursementDate?: string
  rejectionType?: 'cancel' | 'update'
}

export function ExpenseDetails({ id, open, onClose }: ExpenseDetailsProps) {
  const isMobile = useIsMobile()
  const user = useAuthStore((s) => s.user)
  const activeRole = useAuthStore((s) => s.activeRole)
  const permissions = usePermissions()

  const { mutateAsync: approveExpenseMutation, isPending } =
    useApproveExpenseMutation()

  const [openDialog, setOpenDialog] = useState({
    edit: false,
    delete: false,
    viewReceipt: false,
  })
  const [expenseItemId, setExpenseItemId] = useState<string | null>(null)
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  const [submissionAction, setSubmissionAction] = useState<
    'approve' | 'reject'
  >('approve')
  const [rejectionMode, setRejectionMode] = useState<
    'cancel' | 'update' | null
  >(null)

  const form = useForm({
    defaultValues: {
      notes: '',
      reimbursementDate: undefined as Date | undefined,
    },
    onSubmit: async ({ value }) => {
      if (!expense) return

      const isApproval = submissionAction === 'approve'
      const payload: ApprovalData = {
        notes: value.notes,
      }

      if (
        isApproval &&
        permissions.isSystemAdmin() &&
        value.reimbursementDate
      ) {
        payload.reimbursementDate = transformDateToTimezone(
          value.reimbursementDate,
        )
      }

      if (!isApproval && rejectionMode) {
        payload.rejectionType = rejectionMode
      }

      try {
        await approveExpenseMutation({
          _id: expense._id,
          action: submissionAction,
          ...payload,
        })
        handleClose()
      } catch (error) {
        // Error handling is done in the mutation
      }
    },
  })

  const churches = useChurchesStore((s) => s.churches)
  const expenses = useExpensesStore((s) => s.expenses)
  const categories = useExpensesStore((s) => s.categories)
  const users = useUsersStore((s) => s.users)

  const expense = useMemo(
    () => expenses.find((e) => e._id === id),
    [expenses, id],
  )
  const church = useMemo(
    () => churches.find((c) => c._id === expense?.churchId),
    [churches, expense],
  )

  // Approval workflow stages
  const approvalStages = useMemo(() => {
    if (!expense) return []

    // If expense is approved, all steps should be completed
    const isApproved = expense.status === 'approved'

    // Check if any previous stage was rejected
    const localPastorApproved =
      isApproved || !!expense.localPastor?.approvalDate
    const localPastorRejected =
      !isApproved && !!expense.localPastor?.rejectionDate
    const adminPastorApproved =
      isApproved || !!expense.adminPastor?.approvalDate
    const adminPastorRejected =
      !isApproved && !!expense.adminPastor?.rejectionDate
    const adminTreasurerApproved =
      isApproved || !!expense.adminTreasurer?.approvalDate
    const adminTreasurerRejected =
      !isApproved && !!expense.adminTreasurer?.rejectionDate

    return [
      {
        name: 'Local Pastor',
        approval: expense.localPastor,
        completed: localPastorApproved,
        rejected: localPastorRejected,
        inactive: false, // Local pastor is always active
      },
      {
        name: 'Admin Pastor',
        approval: expense.adminPastor,
        // If local pastor rejected, this step should be inactive
        completed: !localPastorRejected && adminPastorApproved,
        rejected: !localPastorRejected && adminPastorRejected,
        inactive: localPastorRejected,
      },
      {
        name: 'Admin Treasurer',
        approval: expense.adminTreasurer,
        // If either local or admin pastor rejected, this step should be inactive
        completed:
          !localPastorRejected &&
          !adminPastorRejected &&
          adminTreasurerApproved,
        rejected:
          !localPastorRejected &&
          !adminPastorRejected &&
          adminTreasurerRejected,
        inactive: localPastorRejected || adminPastorRejected,
      },
    ]
  }, [expense])

  // Calculate current step based on approvals
  const currentStep = useMemo(() => {
    if (!expense) return 1
    if (
      expense.status === 'approved' ||
      (expense.localPastor?.approvalDate &&
        expense.adminPastor?.approvalDate &&
        (!expense.adminTreasurer?.approvalDate ||
          expense.adminTreasurer.rejectionDate))
    )
      return 3
    if (
      expense.localPastor?.approvalDate &&
      (expense.adminPastor?.approvalDate || expense.adminPastor?.rejectionDate)
    )
      return 2
    if (expense.localPastor?.approvalDate) return 2
    return 1
  }, [expense])

  const isUserInvolved = useMemo(
    () =>
      [
        expense?.localPastor?.userId,
        expense?.adminPastor?.userId,
        expense?.adminTreasurer?.userId,
      ].includes(user?._id),
    [
      expense?.localPastor?.userId,
      expense?.adminPastor?.userId,
      expense?.adminTreasurer?.userId,
      user?._id,
    ],
  )
  // Check if user can perform action
  const canApprove = useMemo(() => {
    const pendingExpense = expense?.status === 'pending' && isUserInvolved
    if (pendingExpense && user?._id === expense.localPastor?.userId) {
      return (
        !expense.localPastor?.approvalDate &&
        !expense.localPastor?.rejectionDate
      )
    }
    if (pendingExpense && user?._id === expense.adminPastor?.userId) {
      return (
        expense.localPastor?.approvalDate &&
        !expense.adminPastor?.approvalDate &&
        !expense.adminPastor?.rejectionDate
      )
    }
    if (pendingExpense && user?._id === expense.adminTreasurer?.userId) {
      return (
        expense.adminPastor?.approvalDate &&
        !expense.adminTreasurer?.approvalDate &&
        !expense.adminTreasurer?.rejectionDate
      )
    }
    return false
  }, [expense, activeRole, isUserInvolved])

  const handleClose = () => {
    onClose()
    form.reset()
    setOpenDialog({ edit: false, delete: false, viewReceipt: false })
    setExpandedStep(null)
    setRejectionMode(null)
  }

  const handleStepClick = (index: number) => {
    setExpandedStep(expandedStep === index ? null : index)
  }

  const { data: receiptData, isLoading: isReceiptLoading } =
    useExpenseReceiptQuery(
      id ?? '',
      expenseItemId as string,
      open && !!expense && !!expenseItemId,
    )

  if (!expense) return null

  const renderReceiptContent = () => {
    if (isReceiptLoading) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="text-gray-500">Loading receipt...</p>
          </div>
        </div>
      )
    }
    if (!receiptData) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed">
          <p className="text-gray-500">No receipt available</p>
        </div>
      )
    }
    if (receiptData.mimeType === 'application/pdf') {
      return (
        <object
          data={`${receiptData.url}#toolbar=0&navpanes=0`}
          type="application/pdf"
          className="w-full min-h-150 rounded-md border"
        >
          <p className="text-gray-500 text-sm p-4">
            PDF cannot be displayed.{' '}
            <a
              href={receiptData.url}
              download
              className="text-primary underline"
            >
              Download it
            </a>
            .
          </p>
        </object>
      )
    }
    return (
      <div className="w-full">
        <img
          src={receiptData.url}
          alt="Expense receipt"
          className="w-full h-auto max-h-full object-contain rounded-md border"
        />
      </div>
    )
  }

  const renderApprovalDetails = (stage: (typeof approvalStages)[0]) => {
    if (!stage.approval || !stage.approval.userId) return null

    const approver = users.find((u) => u._id === stage.approval?.userId)

    return (
      <div className="space-y-2">
        {approver && (
          <div>
            <span className="text-xs font-semibold">Approver:</span>
            <p className="text-sm">{approver.name}</p>
          </div>
        )}
        {stage.approval.notes && (
          <div>
            <span className="text-xs font-semibold">Notes:</span>
            <p className="text-sm">{stage.approval.notes}</p>
          </div>
        )}
      </div>
    )
  }

  const footerActions = (
    <div className="flex flex-col lg:flex-row gap-2 lg:justify-end mt-6 pt-4 border-t">
      {!(expense.status === 'approved') && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpenDialog((prev) => ({ ...prev, edit: true }))}
          className="flex items-center gap-2 w-full lg:w-auto"
        >
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
      )}
      {permissions.isSystemAdmin() && !(expense.status === 'approved') && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setOpenDialog((prev) => ({ ...prev, delete: true }))}
          className="flex items-center gap-2 w-full lg:w-auto"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      )}
    </div>
  )

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        trigger={null}
        title={`Expense Details - ${expense.refNumber}`}
        contentClassName={`min-w-[98%] lg:min-w-[80%] ${expense.status === 'approved' ? '' : 'lg:min-h-[90vh]'}`}
        direction="right"
      >
        <div
          className={`grid gap-6 ${
            !isMobile && expenseItemId ? 'grid-cols-2' : 'grid-cols-1'
          }`}
        >
          {/* Left Side - Expense Details */}
          <div className="space-y-4">
            {/* Expense Overview */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p className="text-2xl font-semibold">
                      {formatCurrency(expense.amount)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-red-700" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="text-lg font-semibold">
                      {formatDate(expense.date, 'MMMM D, YYYY')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Approval Progress */}
            <div className="bg-white border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                Approval Progress
              </h3>

              <InteractiveStepper
                defaultValue={currentStep}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                className="w-full"
              >
                {approvalStages.map((stage, index) => (
                  <InteractiveStepperItem
                    key={stage.name}
                    completed={stage.completed}
                    rejected={stage.rejected}
                    disabled={stage.inactive}
                  >
                    <div className="flex flex-col w-full">
                      <div
                        className={`flex items-center cursor-pointer rounded-lg p-2 -m-2 transition-colors hover:bg-gray-200 ${
                          expandedStep === index && !isMobile
                            ? 'bg-gray-300 '
                            : ''
                        }`}
                        onClick={() => handleStepClick(index)}
                      >
                        <InteractiveStepperIndicator />
                        <div className="flex flex-col ml-3">
                          <InteractiveStepperTitle>
                            {stage.name}
                          </InteractiveStepperTitle>
                          {stage.approval?.approvalDate && (
                            <span className="text-xs text-green-600 mt-1">
                              {formatDate(
                                stage.approval.approvalDate,
                                'MMM D, YYYY h:mm A',
                              )}
                            </span>
                          )}
                          {stage.approval?.rejectionDate && (
                            <span className="text-xs text-red-600 mt-1">
                              {formatDate(
                                stage.approval.rejectionDate,
                                'MMM D, YYYY h:mm A',
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Mobile: Expanded Details Below the step */}
                      {isMobile &&
                        expandedStep === index &&
                        approvalStages[expandedStep] && (
                          <div className="mt-3 ml-12 animate-in slide-in-from-top-2">
                            <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-primary">
                              {renderApprovalDetails(
                                approvalStages[expandedStep],
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                    <InteractiveStepperSeparator />
                  </InteractiveStepperItem>
                ))}
              </InteractiveStepper>

              {/* Desktop: Expanded Details Dropdown (below) */}
              {!isMobile &&
                expandedStep !== null &&
                approvalStages[expandedStep] && (
                  <div className="mt-3 pt-4 animate-in slide-in-from-top-2">
                    <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-primary">
                      {renderApprovalDetails(approvalStages[expandedStep])}
                    </div>
                  </div>
                )}
            </div>

            {/* Expense Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div>
                    <span className="font-medium text-gray-600">
                      Requester:
                    </span>
                    <p className="text-gray-900">{expense.requester}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div>
                    <span className="font-medium text-gray-600">Email:</span>
                    <p className="text-gray-900">{expense.requesterEmail}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <div>
                    <span className="font-medium text-gray-600">Church:</span>
                    <p className="text-gray-900">
                      {church ? church.name : 'Unknown Church'}
                    </p>
                  </div>
                </div>
                {expense.status === 'approved' && (
                  <div className="flex items-start gap-3">
                    <div>
                      <span className="font-medium text-gray-600">
                        Reimbursement Date:
                      </span>
                      <p className="text-gray-900">
                        {expense.adminTreasurer?.reimbursementDate
                          ? formatDate(
                              expense.adminTreasurer.reimbursementDate,
                              'MMMM D, YYYY',
                            )
                          : 'Unknown Date'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ExpenseItems */}
            <div>
              <span className="font-medium text-gray-600">Items:</span>
              <div className="max-h-60 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                <div className="space-y-2 p-1 pr-4">
                  {expense.expenseItems.map((item, index) => {
                    const category = categories.find(
                      (c) => c._id === item.categoryId,
                    )
                    return (
                      <div
                        key={item._id || index}
                        onClick={() => {
                          if (item.receiptUrl) {
                            if (isMobile) {
                              setExpenseItemId(item._id)
                              setOpenDialog((prev) => ({
                                ...prev,
                                viewReceipt: true,
                              }))
                            } else {
                              // Toggle selection on desktop
                              setExpenseItemId(
                                expenseItemId === item._id ? null : item._id,
                              )
                            }
                          }
                        }}
                        className={`bg-gray-50 p-2 rounded-lg border transition-all ${
                          item.receiptUrl
                            ? 'cursor-pointer hover:bg-gray-100 hover:border-gray-400'
                            : ''
                        } ${
                          !isMobile && expenseItemId === item._id
                            ? 'ring-2 ring-primary border-primary'
                            : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <Tag className="h-4 w-4 text-gray-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {item.itemDescription}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {category?.name || 'Unknown Category'}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 text-green-600">
                                <span className="text-lg font-semibold whitespace-nowrap">
                                  {formatCurrency(item.amount)}
                                </span>
                              </div>
                            </div>
                          </div>
                          {item.receiptUrl && (
                            <ReceiptText className="h-4 w-4 text-gray-500 shrink-0" />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Notes */}
            {expense.notes && (
              <div>
                <span className="font-medium text-gray-600">Comments:</span>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {expense.notes}
                  </p>
                </div>
              </div>
            )}

            {/* Approval Form */}
            {canApprove && (
              <div className="bg-white border rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700">
                  Your Decision
                </h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    form.handleSubmit()
                  }}
                >
                  <form.Field
                    name="notes"
                    children={(field) => (
                      <div className="gap-0">
                        <FormTextarea
                          field={field}
                          label="Comments"
                          id={field.name}
                          name={field.name}
                        />
                      </div>
                    )}
                  />

                  {permissions.isSystemAdmin() && (
                    <form.Field
                      name="reimbursementDate"
                      children={(field) => (
                        <div className="space-y-2">
                          <DatePicker
                            field={field}
                            label="Reimbursement Payment Date"
                            id={field.name}
                            name={field.name}
                            startYear={2000}
                            disableFutureDates
                            required
                          />
                          <p className="text-xs text-gray-500">
                            Required to approve the expense
                          </p>
                        </div>
                      )}
                    />
                  )}

                  <form.Subscribe
                    selector={(state) => [state.values.reimbursementDate]}
                    children={([reimbursementDateValue]) => {
                      const canSubmitApproval =
                        !permissions.isSystemAdmin() || reimbursementDateValue

                      if (rejectionMode !== null) {
                        return (
                          <div className="space-y-3 pt-2">
                            <div className="space-y-1">
                              <label className="text-sm font-medium text-gray-700">
                                Rejection Type
                              </label>
                              <Select
                                value={rejectionMode}
                                onValueChange={(v) =>
                                  setRejectionMode(v as 'cancel' | 'update')
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="cancel">
                                    Cancel Request
                                  </SelectItem>
                                  <SelectItem value="update">
                                    Request Correction
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => setRejectionMode(null)}
                                disabled={isPending}
                              >
                                Back
                              </Button>
                              <Button
                                type="submit"
                                variant="destructive"
                                className="flex-1"
                                onClick={() => setSubmissionAction('reject')}
                                disabled={isPending}
                              >
                                {isPending && submissionAction === 'reject'
                                  ? 'Submitting...'
                                  : 'Confirm Rejection'}
                              </Button>
                            </div>
                          </div>
                        )
                      }

                      return (
                        <div className="flex gap-2 pt-2">
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setRejectionMode('cancel')}
                            disabled={isPending}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            type="submit"
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => setSubmissionAction('approve')}
                            disabled={!canSubmitApproval || isPending}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {isPending && submissionAction === 'approve'
                              ? 'Approving...'
                              : 'Approve'}
                          </Button>
                        </div>
                      )
                    }}
                  />
                </form>
              </div>
            )}

            {expense.status === 'cancelled' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  This reimbursement request has been cancelled.
                </p>
              </div>
            )}

            {expense.status === 'updating' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  A correction has been requested. Awaiting the requester to
                  resubmit.
                </p>
              </div>
            )}

            {expense.status === 'pending' && isUserInvolved && !canApprove && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  This expense is pending approval from a previous stage.
                </p>
              </div>
            )}

            {/* Actions Footer */}
            {footerActions}
          </div>

          {/* Right Side - Receipt (Desktop Only) */}
          {!isMobile && expenseItemId && (
            <div className="border-l p-4 overflow-y-auto max-h-[75vh] lg:max-h-[80vh]">
              {renderReceiptContent()}
            </div>
          )}
        </div>
      </Modal>
      {/* Mobile Receipt Modal */}
      {isMobile && (
        <Modal
          open={openDialog.viewReceipt}
          onClose={() =>
            setOpenDialog((prev) => ({ ...prev, viewReceipt: false }))
          }
          trigger={null}
          title="Expense Receipt"
          contentClassName="max-w-3xl"
        >
          {renderReceiptContent()}
        </Modal>
      )}
      <ExpenseForm
        type={'edit'}
        id={expense._id}
        open={openDialog.edit}
        onClose={() => setOpenDialog((prev) => ({ ...prev, edit: false }))}
      />
      <DeleteExpense
        id={expense._id}
        open={openDialog.delete}
        onClose={() => setOpenDialog((prev) => ({ ...prev, delete: false }))}
      />
    </>
  )
}
